// Small helpers shared by the Table and Gantt views.
import { useCallback, useEffect, useState } from 'react';
import type { Location, Task, ViewSettings } from '@shared/types';
import { getDb } from '@/lib/store';
import { effectiveStatuses } from '@/lib/hierarchy';
import type { TaskGroup } from '@/lib/query';
import { createTask, type NewTask } from '@/lib/actions';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { StatusPill } from '@/components/fields/Status';

/** Group header label: status pill for status groups, avatar / icon / color dot otherwise. */
export function GroupLabel({ group }: { group: TaskGroup }) {
  if (group.status) return <StatusPill status={group.status} size="md" />;
  const isUser = group.color && group.defaults.assigneeIds?.length;
  return (
    <span className="grp-label">
      {isUser ? (
        <Avatar userId={group.key} size={18} />
      ) : group.icon ? (
        <Icon name={group.icon} size={14} color={group.color ?? 'var(--text-2)'} fill={group.icon === 'Flag' && group.color ? group.color : 'none'} />
      ) : group.color ? (
        <span className="dot" style={{ background: group.color, width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
      ) : null}
      <span className="truncate">{group.label}</span>
    </span>
  );
}

/** The list a new task created in this location / group lands in. */
export function defaultListId(location: Location, group?: TaskGroup): string | undefined {
  if (group?.defaults.listId) return group.defaults.listId;
  if (location.type === 'list') return location.id;
  const db = getDb();
  const lists = Object.values(db.lists)
    .filter(
      (l) =>
        !l.archived &&
        (location.type === 'folder'
          ? l.folderId === location.id
          : location.type === 'space'
            ? l.spaceId === location.id
            : db.spaces[l.spaceId]?.workspaceId === location.id),
    )
    .sort((a, b) => a.orderIndex - b.orderIndex);
  return lists[0]?.id;
}

/** Create a task applying the group's defaults (status mapped by name onto the target list). */
export function createTaskInGroup(location: Location, group: TaskGroup | undefined, name: string, extra: Partial<NewTask> = {}): Task | null {
  const listId = extra.listId ?? defaultListId(location, group);
  if (!listId || !name.trim()) return null;
  let statusId = group?.defaults.statusId;
  if (!statusId && group?.status) {
    statusId = effectiveStatuses(getDb(), listId).find((s) => s.name.toLowerCase() === group.status!.name.toLowerCase())?.id;
  }
  const d = group?.defaults ?? {};
  return createTask({
    listId,
    name,
    statusId,
    parentId: null,
    assigneeIds: d.assigneeIds ?? [],
    priority: d.priority ?? null,
    taskTypeId: (d.taskTypeId as string | null | undefined) ?? null,
    tagIds: d.tagIds,
    customFields: d.customFields,
    ...extra,
  });
}

/**
 * Expanded state for subtask carets. 'expanded' starts with every parent open,
 * 'collapsed' with every parent closed; the user's toggles invert that default.
 */
export function useSubtaskExpansion(mode: ViewSettings['subtasks']) {
  const [toggled, setToggled] = useState<Set<string>>(() => new Set());
  useEffect(() => setToggled(new Set()), [mode]);
  const isExpanded = useCallback((id: string) => (mode === 'expanded') !== toggled.has(id), [mode, toggled]);
  const toggle = useCallback(
    (id: string) =>
      setToggled((prev) => {
        const n = new Set(prev);
        if (n.has(id)) n.delete(id);
        else n.add(id);
        return n;
      }),
    [],
  );
  return { isExpanded, toggle };
}
